"use client";

import { type StorefrontUrls } from "@/app/utilities/storefront-urls";
import { Breadcrumbs } from "@/composites/navigation";
import {
	PlpFacetRail,
	PlpHero,
	PlpPagination,
	PlpPopularFilters,
	PlpProductGrid,
	PlpResultsToolbar
} from "@/composites/product-listing";
import { type Locale } from "@/domain/catalog";
import { type SuperCategory } from "@/domain/product-listing";
import {
	Col as DSCol,
	Container,
	Grid,
	type ColProps
} from "@pamperedchef/design-system";
import { type FC, type ReactNode } from "react";
import { buildPlpBreadcrumbItems } from "./helpers/build-plp-breadcrumb-items";
import {
	ProductListingResults,
	type ProductListingResultsProps,
	type ProductListingSearchCredentials
} from "./ProductListingResults";

const Col = DSCol as FC<ColProps & { children?: ReactNode }>;

// Relative-href default for stories / tests that don't pass `urls`. Real
// storefront URLs are resolved server-side via `getStorefrontUrls()` in the
// route and threaded down — never resolved inside this component.
const DEFAULT_HOME_URL = "/";

export interface ProductListingTemplateProps {
	category: SuperCategory;
	locale: Locale;
	search: ProductListingSearchCredentials;
	urls?: Pick<StorefrontUrls, "home">;
	searchClient?: ProductListingResultsProps["searchClient"];
}

export function ProductListingTemplate({
	category,
	locale,
	search,
	urls,
	searchClient
}: ProductListingTemplateProps): JSX.Element {
	return (
		<article
			data-template="ProductListingTemplate"
			data-category={category.slug}
		>
			<Container>
				<Grid columns={12} gap="lg">
					<Col size={12}>
						<Breadcrumbs
							items={buildPlpBreadcrumbItems(category, {
								homeUrl: urls?.home ?? DEFAULT_HOME_URL
							})}
						/>
					</Col>

					<Col size={12}>
						<PlpHero title={category.name} />
					</Col>

					<Col size={12}>
						<PlpPopularFilters />
					</Col>

					<ProductListingResults
						category={category}
						locale={locale}
						search={search}
						searchClient={searchClient}
					>
						<Col size={12} md={3}>
							<PlpFacetRail />
						</Col>

						<Col size={12} md={9}>
							<PlpResultsToolbar locale={locale} />
							<PlpProductGrid locale={locale} />
							<PlpPagination />
						</Col>
					</ProductListingResults>
				</Grid>
			</Container>
		</article>
	);
}
